
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Outlet, useParams, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/layout/Layout';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { AnimationProvider } from './context/AnimationContext';
import ErrorBoundary from './components/shared/ErrorBoundary';
import { DataProvider } from './context/DataContext';
import { useTranslation } from 'react-i18next';
import { supportedLngs } from './i18n';

// Public, indexable pages are imported directly (crawlers must see them in the first render). Pages that
// only staff or signed-in clients use are split into their own chunks (esbuild --splitting).
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import PracticeAreasPage from './pages/PracticeAreasPage';
import ResultsPage from './pages/ResultsPage';
import ReviewsPage from './pages/ReviewsPage';
import ContactPage from './pages/ContactPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
const ClientPortalPage = lazy(() => import('./pages/ClientPortalPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const BlogFormPage = lazy(() => import('./pages/BlogFormPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import AttorneyProfilePage from './pages/AttorneyProfilePage';
import NotFoundPage from './pages/NotFoundPage';
import PracticeAreaDetailPage from './pages/PracticeAreaDetailPage';

// This component detects the language from the URL, sets it, and renders the nested routes.
const LanguageWrapper = () => {
  const { lang } = useParams<{ lang?: string }>();
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    // If lang is provided and valid, change language
    if (lang && supportedLngs.includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    } else if (!lang && i18n.language !== 'en') {
      // If no lang is provided in URL, force English
      i18n.changeLanguage('en');
    }
  }, [lang, i18n]);

  // If lang is provided but invalid, redirect to 404
  if (lang && !supportedLngs.includes(lang)) {
    return <Navigate to="/404" state={{ from: location }} replace />;
  }

  // If no lang is provided, default to English (or i18n.language)
  // We don't redirect, we just render the outlet so /about works without /en/about
  return <Outlet />;
};

// /locations used to render the Contact page under a second URL (duplicate title, meta description and
// content). The edge function 301s it for a first load; this covers in-app navigation.
const LocationsRedirect = () => {
    const { lang } = useParams<{ lang?: string }>();
    return <Navigate to={lang && supportedLngs.includes(lang) ? `/${lang}/contact/` : '/contact/'} replace />;
};

// This component handles the initial redirect from the root URL based on language.
const RootRedirect = () => {
    const { i18n } = useTranslation();
    
    // If the detected language is not English, redirect to the language-specific root.
    if (i18n.language && i18n.language !== 'en' && supportedLngs.includes(i18n.language)) {
        return <Navigate to={`/${i18n.language}`} replace />;
    }
    
    // Otherwise, render the English HomePage directly on the root URL.
    return <HomePage />;
};

function App(): React.ReactNode {
  const [Router, setRouter] = useState<typeof BrowserRouter | typeof HashRouter>(() => BrowserRouter);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (window.location.href.includes('.scf.usercontent.goog')) {
        setRouter(() => HashRouter);
      }
    }
  }, []);

  return (
    <AnimationProvider>
      <AuthProvider>
        <Router>
          {/* DEFINITIVE FIX: The DataProvider now wraps the entire Layout.
              This ensures that all components within the layout, including the Navbar and Footer,
              have access to the data context, resolving the "useData must be used within a DataProvider" error.
              The DataProvider itself listens for language changes from i18next and reloads data accordingly.
          */}
          <DataProvider>
            <Layout>
                <ErrorBoundary>
                  <Suspense fallback={null}>
                  <Routes>
                    {/* Public, translated routes nested under a language code */}
                    <Route path="/:lang" element={<LanguageWrapper />}>
                      <Route index element={<HomePage />} />
                      <Route path="about" element={<AboutPage />} />
                      <Route path="practice-areas" element={<PracticeAreasPage />} />
                      <Route path="practice-areas/:slug" element={<PracticeAreaDetailPage />} />
                      <Route path="results" element={<ResultsPage />} />
                      <Route path="reviews" element={<ReviewsPage />} />
                      <Route path="contact" element={<ContactPage />} />
                      <Route path="locations" element={<LocationsRedirect />} />
                      <Route path="blog" element={<BlogPage />} />
                      <Route path="blog/:slug" element={<BlogPostPage />} />
                      <Route path="client-portal" element={<ClientPortalPage />} />
                      <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                      <Route path="terms-and-conditions" element={<TermsAndConditionsPage />} />
                      <Route path="attorneys/:slug" element={<AttorneyProfilePage />} />
                    </Route>

                    {/* Public routes for default language (no prefix) */}
                    <Route element={<LanguageWrapper />}>
                      <Route path="about" element={<AboutPage />} />
                      <Route path="practice-areas" element={<PracticeAreasPage />} />
                      <Route path="practice-areas/:slug" element={<PracticeAreaDetailPage />} />
                      <Route path="results" element={<ResultsPage />} />
                      <Route path="reviews" element={<ReviewsPage />} />
                      <Route path="contact" element={<ContactPage />} />
                      <Route path="locations" element={<LocationsRedirect />} />
                      <Route path="blog" element={<BlogPage />} />
                      <Route path="blog/:slug" element={<BlogPostPage />} />
                      <Route path="client-portal" element={<ClientPortalPage />} />
                      <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                      <Route path="terms-and-conditions" element={<TermsAndConditionsPage />} />
                      <Route path="attorneys/:slug" element={<AttorneyProfilePage />} />
                    </Route>

                    {/* Non-translated, internal routes */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/auth/callback" element={<AuthCallbackPage />} />
                    <Route element={<ProtectedRoute />}>
                      <Route path="/admin" element={<AdminPage />} />
                      <Route path="/admin/blog/new" element={<BlogFormPage />} />
                      <Route path="/admin/blog/edit/:slug" element={<BlogFormPage />} />
                    </Route>
                    
                    {/* Root redirect and final 404 fallback */}
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                  </Suspense>
                </ErrorBoundary>
            </Layout>
          </DataProvider>
        </Router>
      </AuthProvider>
    </AnimationProvider>
  );
}

export default App;
